// @ts-nocheck
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkScalarsToColors from '@kitware/vtk.js/Common/Core/ScalarsToColors';
import Constants from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction/Constants';
import macro from '@kitware/vtk.js/macros';

/* eslint-disable @typescript-eslint/naming-convention --
 * we don't have control over this
 */
const {ColorSpace, Scale} = Constants;
const {ScalarMappingTarget} = vtkScalarsToColors;
// eslint-enable
const {vtkWarningMacro} = macro;

// ----------------------------------------------------------------------------
// vtkColorTransferFunction methods
// ----------------------------------------------------------------------------

function vtkExtendedColorTransferFunction(publicAPI, model) {
	// Set our className
	model.classHierarchy.push('vtkExtendedColorTransferFunction');

	publicAPI.setThresholdRange = (lower, upper) => {
		model.thresholdRange = [lower, upper];

		publicAPI.modified();
	};

	//----------------------------------------------------------------------------
	publicAPI.mapData = (input, output, outFormat, inputOffset: number) => {
		if (publicAPI.getSize() === 0) {
			vtkWarningMacro('Transfer Function Has No Points!');
			return;
		}

		const alpha = Math.floor(publicAPI.getAlpha() * 255.0 + 0.5);
		const length = input.getNumberOfTuples();
		const inIncr = input.getNumberOfComponents();

		const outputV = output.getData();
		const inputV = input.getData();
		const rgb = [];

		if (outFormat === ScalarMappingTarget.RGBA) {
			const {thresholdRange} = model;

			const lowerRange = thresholdRange[0];
			const upperRange = thresholdRange[1];

			for (let i = 0; i < length; i++) {
				const x = inputV[i * inIncr + inputOffset];
				publicAPI.getColor(x, rgb);
				outputV[i * 4] = Math.floor(rgb[0] * 255.0 + 0.5);
				outputV[i * 4 + 1] = Math.floor(rgb[1] * 255.0 + 0.5);
				outputV[i * 4 + 2] = Math.floor(rgb[2] * 255.0 + 0.5);
				if (x > upperRange || x < lowerRange) {
					outputV[i * 4 + 3] = 0;
				} else {
					outputV[i * 4 + 3] = alpha;
				}
			}
		}

		if (outFormat === ScalarMappingTarget.RGB) {
			for (let i = 0; i < length; i++) {
				const x = inputV[i * inIncr + inputOffset];
				publicAPI.getColor(x, rgb);
				outputV[i * 3] = Math.floor(rgb[0] * 255.0 + 0.5);
				outputV[i * 3 + 1] = Math.floor(rgb[1] * 255.0 + 0.5);
				outputV[i * 3 + 2] = Math.floor(rgb[2] * 255.0 + 0.5);
			}
		}

		if (outFormat === ScalarMappingTarget.LUMINANCE) {
			for (let i = 0; i < length; i++) {
				const x = inputV[i * inIncr + inputOffset];
				publicAPI.getColor(x, rgb);
				outputV[i] = Math.floor(
					rgb[0] * 76.5 + rgb[1] * 150.45 + rgb[2] * 28.05 + 0.5,
				);
			}
		}

		if (outFormat === ScalarMappingTarget.LUMINANCE_ALPHA) {
			for (let i = 0; i < length; i++) {
				const x = inputV[i * inIncr + inputOffset];
				publicAPI.getColor(x, rgb);
				outputV[i * 2] = Math.floor(
					rgb[0] * 76.5 + rgb[1] * 150.45 + rgb[2] * 28.05 + 0.5,
				);
				outputV[i * 2 + 1] = alpha;
			}
		}
	};
}

// ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------

const DEFAULT_VALUES = {
	clamping: true,
	colorSpace: ColorSpace.RGB,
	hSVWrap: true,
	scale: Scale.LINEAR,

	nanColor: null,
	belowRangeColor: null,
	aboveRangeColor: null,
	useAboveRangeColor: false,
	useBelowRangeColor: false,

	allowDuplicateScalars: false,

	table: null,
	tableSize: 0,
	buildTime: null,

	nodes: null,

	discretize: false,
	numberOfValues: 256,
};

// ----------------------------------------------------------------------------

export function extend(publicAPI, model, initialValues = {}) {
	Object.assign(model, DEFAULT_VALUES, initialValues);

	// Inheritance
	vtkColorTransferFunction.extend(publicAPI, model, initialValues);

	// Object specific methods
	vtkExtendedColorTransferFunction(publicAPI, model);
}

// ----------------------------------------------------------------------------

export const newInstance = macro.newInstance(
	extend,
	'vtkExtendedColorTransferFunction',
);

// ----------------------------------------------------------------------------

export default {newInstance, extend, ...Constants};
